// Structured LLM client used by advisors, S-2 assessments, FRAGO parsing, and the debrief narrative.
// Talks to the dev/Cloudflare proxy at `/api/llm` (never the model directly - the key never reaches the
// browser), validates the JSON it gets back with the caller's zod schema, and never throws: on any
// failure (network, timeout, non-2xx, schema mismatch) it resolves to the caller's template. Spec §6
// "fallbacks: a 6-second timeout swaps templates in; the sim never blocks on the model." Plan Task 16.
import type { z } from 'zod';
import { zodToJsonSchema } from './schemas';
import * as audit from './audit';
import type { AuditPurpose } from './audit';
import { cacheGet, cacheKey, cacheMode, cachePut, installDumpHook, promptHint, warmCache } from './cache';

export interface CallLLMRequest<T> {
  model: 'sonnet' | 'haiku';
  system: string;
  user: string;
  schema: z.ZodType<T>;
  timeoutMs?: number;
  /**
   * Answer budget in tokens. The proxy's default (2048) fits a play, an assessment or a parsed FRAGO
   * with room to spare; the debrief's six cited paragraphs do not, and a truncated answer is not valid
   * JSON, so it is thrown away and the template stands in. Raise it for the long ones only — the
   * budget is also the worst case the player waits for.
   */
  maxTokens?: number;
  /**
   * How hard the model should think before answering, when the provider supports it.
   *
   * `'low'` is not a quality shortcut here, it is what makes an interactive answer possible: measured
   * on the real advisor prompt (2026-09-21), the default effort put the first character of the answer
   * 23 s after the request and sometimes spent the whole answer budget thinking and returned no text
   * at all. See `ProxyRequest.effort` in `src/ai/proxyCore.ts` for the numbers.
   */
  effort?: 'low' | 'medium' | 'high';
  /**
   * What this call is for, for the AI audit (`src/ai/audit.ts`, rendered in the design record). Call
   * sites need only pass the string; everything else on the row is measured here. Left out, it is
   * inferred from the system block, so a call site this work does not own still gets a labelled row.
   */
  purpose?: AuditPurpose;
  /** What names this particular call - an advisor persona, an event id, a play name. */
  auditRef?: string;
  /**
   * Called with the answer as it is being written, whenever more of it arrives.
   *
   * Passing it switches the call to the streaming path: the proxy asks the provider to stream and
   * forwards the text as Server-Sent Events, so a caller can show something ten seconds before the
   * object is complete enough to validate. The argument is the whole answer so far, not the delta,
   * because every caller so far wants to run a tolerant parse over the accumulated text.
   *
   * Everything else is unchanged - same schema validation, same audit row, same template fallback -
   * and a backend that cannot stream simply calls this once, or not at all, before resolving.
   */
  onText?: (partial: string) => void;
}

export interface CallLLMResult<T> {
  value: T;
  source: 'llm' | 'template';
  /**
   * True when the value is a recorded model answer replayed from `src/ai/cache.ts` rather than a live
   * call. It is still the model's words, so `source` stays 'llm'; the flag is there so the interface
   * can say "cached" where that matters (the debrief narrative chip does).
   */
  cached?: boolean;
  error?: string;
  /** The row this call opened in the AI audit, for a caller that wants to attach its validation to it. */
  auditId?: number;
  /** `usage.cache_read_input_tokens` from the provider: the prompt prefix served from its cache. */
  cachedTokens?: number;
}

const DEFAULT_TIMEOUT_MS = 6000;

/**
 * The purpose of a call that did not name one, read off the system block's first clause. Only the
 * report feed's two calls take this path (its store is owned elsewhere); both system blocks are
 * constants in `src/ai/feedPrompts.ts`, so the match is against text this repository owns.
 */
function inferPurpose(system: string): AuditPurpose {
  if (system.startsWith('You are the S-2')) return 's2';
  if (system.startsWith('You are the battle captain')) return 'sitrep';
  return 'other';
}

/** What names a call that did not name itself: the feed event the answer is being written about. */
function inferRef(purpose: AuditPurpose, user: string): string | undefined {
  if (purpose === 's2') {
    const m = /Triggering event: #(\d+)/.exec(user);
    return m ? `event #${m[1]}` : 'report feed';
  }
  if (purpose === 'sitrep') return 'report feed';
  return undefined;
}

// The demo replay cache starts loading with the module, not with the first call: the whole point of it
// is the run where the network has gone away (plan Task 21, `src/ai/cache.ts`).
warmCache();
if (cacheMode() === 'record') installDumpHook();

/** Calls the LLM proxy for a structured result matching `req.schema`, falling back to `fallback()` within
 * `req.timeoutMs` (default 6000 ms) on any error. Never throws. */
export async function callLLM<T>(req: CallLLMRequest<T>, fallback: () => T): Promise<CallLLMResult<T>> {
  const timeoutMs = req.timeoutMs ?? DEFAULT_TIMEOUT_MS;
  const mode = cacheMode();
  const key = cacheKey({ model: req.model, system: req.system, user: req.user });

  // One audit row per call, opened here and closed by `settle` on every path out of this function, so
  // the page can show a call that is still out as well as one that came back (src/ai/audit.ts).
  const startedAt = performance.now();
  const purpose = req.purpose ?? inferPurpose(req.system);
  const auditId = audit.record({
    purpose,
    model: req.model,
    promptChars: req.system.length + req.user.length,
    ref: req.auditRef ?? inferRef(purpose, req.user),
  });

  /** Closes the audit row, tags the answer so the validator can find it, and returns the result. */
  const settle = (result: CallLLMResult<T>): CallLLMResult<T> => {
    audit.note(auditId, {
      latencyMs: Math.round(performance.now() - startedAt),
      source: result.cached ? 'cached' : result.source,
      error: result.error,
      cachedTokens: result.cachedTokens,
    });
    audit.tag(result.value, auditId);
    return { ...result, auditId };
  };

  /** A recorded answer for this exact call, if there is one and it still matches the schema. */
  const fromCache = async (): Promise<T | undefined> => {
    const hit = await cacheGet(key);
    if (hit === undefined) return undefined;
    const parsed = req.schema.safeParse(hit);
    return parsed.success ? parsed.data : undefined;
  };

  // Read mode is the offline demo: answer from the recording and never touch the network.
  if (mode === 'read') {
    const hit = await fromCache();
    if (hit !== undefined) return settle({ value: hit, source: 'llm', cached: true });
  }

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);

  try {
    const streaming = req.onText !== undefined;
    const res = await fetch('/api/llm', {
      method: 'POST',
      headers: {
        'content-type': 'application/json',
        ...(streaming ? { accept: 'text/event-stream' } : {}),
      },
      body: JSON.stringify({
        model: req.model,
        system: req.system,
        user: req.user,
        schema: zodToJsonSchema(req.schema),
        maxTokens: req.maxTokens,
        ...(req.effort ? { effort: req.effort } : {}),
        ...(streaming ? { stream: true } : {}),
      }),
      signal: controller.signal,
    });

    // A proxy that cannot stream (the OpenAI-compatible development backend, or a dev server without
    // the streaming middleware) answers the same request with plain JSON. Both shapes end here.
    let firstTextAt: number | undefined;
    const onText = req.onText && ((partial: string) => {
      if (firstTextAt === undefined) {
        firstTextAt = performance.now();
        audit.note(auditId, { firstTextMs: Math.round(firstTextAt - startedAt) });
      }
      req.onText?.(partial);
    });
    const answer = isEventStream(res) ? await readProxyStream(res, onText) : await readProxyJson(res);
    if (!answer.ok) throw new Error(answer.error);

    const parsed = req.schema.safeParse(answer.json);
    if (!parsed.success) throw new Error(`llm response failed schema validation: ${parsed.error.message}`);

    if (mode === 'record') {
      cachePut(key, { model: req.model, hint: promptHint(req.user), json: parsed.data });
    }
    return settle({ value: parsed.data, source: 'llm', cachedTokens: answer.cachedTokens });
  } catch (err) {
    const error = err instanceof Error ? err.message : String(err);
    // The call failed — timeout, offline, a proxy without a key. A recorded answer is a better demo
    // than a template, so it gets the last word before the fallback does.
    const hit = await fromCache();
    if (hit !== undefined) return settle({ value: hit, source: 'llm', cached: true, error });
    return settle({ value: fallback(), source: 'template', error });
  } finally {
    clearTimeout(timer);
  }
}

// ---- reading the two answer shapes the proxy can send ------------------------------------------------

/** What either reader hands back: the answer object, or why there is not one. */
type ProxyAnswer =
  | { ok: true; json: unknown; cachedTokens?: number }
  | { ok: false; error: string };

function isEventStream(res: Response): boolean {
  // Defensive about `headers` and `body`: a non-streaming backend, and the test doubles that stand in
  // for one, answer with a plain object that has neither.
  const type = res.headers?.get?.('content-type') ?? '';
  return type.includes('text/event-stream') && Boolean(res.body);
}

/** The plain JSON shape: `{ok, json, usage?}` or `{ok:false, error}`. */
async function readProxyJson(res: Response): Promise<ProxyAnswer> {
  const data: unknown = await res.json().catch(() => null);
  if (!res.ok || !data || typeof data !== 'object' || (data as { ok?: unknown }).ok !== true) {
    const err = data && typeof data === 'object' ? (data as { error?: unknown }).error : undefined;
    return { ok: false, error: typeof err === 'string' ? err : `llm proxy returned status ${res.status}` };
  }
  return {
    ok: true,
    json: (data as { json: unknown }).json,
    cachedTokens: usageCacheRead(data),
  };
}

/**
 * The streaming shape: `text` events carrying runs of characters, then exactly one `done` carrying the
 * same object the JSON path would have returned (see `src/ai/proxyCore.ts` for the frame format).
 *
 * `onText` is called with the whole answer so far, not the delta, because the callers all want to run
 * a tolerant parse over the accumulated text rather than reassemble it themselves.
 */
async function readProxyStream(res: Response, onText?: (partial: string) => void): Promise<ProxyAnswer> {
  const reader = res.body!.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let accumulated = '';
  let done: ProxyAnswer | null = null;

  const handleFrame = (frame: string): void => {
    let event = 'message';
    const data: string[] = [];
    for (const line of frame.split('\n')) {
      if (line.startsWith('event:')) event = line.slice(6).trim();
      else if (line.startsWith('data:')) data.push(line.slice(5).trim());
    }
    if (data.length === 0) return;
    let payload: unknown;
    try { payload = JSON.parse(data.join('\n')); } catch { return; }

    if (event === 'text') {
      const text = (payload as { text?: unknown }).text;
      if (typeof text === 'string' && text.length > 0) {
        accumulated += text;
        onText?.(accumulated);
      }
      return;
    }
    if (event !== 'done') return;
    const p = payload as { ok?: unknown; json?: unknown; error?: unknown };
    done = p.ok === true
      ? { ok: true, json: p.json, cachedTokens: usageCacheRead(payload) }
      : { ok: false, error: typeof p.error === 'string' ? p.error : 'llm proxy stream failed' };
  };

  for (;;) {
    const { done: finished, value } = await reader.read();
    if (finished) break;
    buffer += decoder.decode(value, { stream: true });
    let split: number;
    // Server-Sent Events are separated by a blank line; anything after the last one is a partial frame.
    while ((split = buffer.indexOf('\n\n')) !== -1) {
      handleFrame(buffer.slice(0, split));
      buffer = buffer.slice(split + 2);
    }
  }
  if (buffer.trim().length > 0) handleFrame(buffer);

  return done ?? { ok: false, error: 'llm proxy stream ended without an answer' };
}

/** `usage.cacheReadTokens` out of either answer shape, when the provider reported one. */
function usageCacheRead(data: unknown): number | undefined {
  const usage = (data as { usage?: { cacheReadTokens?: unknown } } | null)?.usage;
  return typeof usage?.cacheReadTokens === 'number' ? usage.cacheReadTokens : undefined;
}
